import {
  RouteCtxProvider,
  useLocale,
  useLocalePath,
} from "@commercekit/remix/lib/hooks/useLocale";
import { CommercekitCtx } from "@commercekit/remix/lib/provider/CommercekitCtx";
import { asText } from "@prismicio/helpers";
import type { LinkProps } from "@prismicio/react";
import type { RichTextField, TitleField } from "@prismicio/types";
import {
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useLoaderData,
  useLocation,
  useNavigationType,
} from "@remix-run/react";
import type { FC } from "react";
import * as React from "react";
import { useMemo } from "react";
import { ErrorAlertCtxProvider } from "~/hooks/useErrorAlert";
import { GlobalCtxProvider, useGlobalCtx } from "~/store/globalState";
import AppFooter from "./WvAppFooter/AppFooter";
import AppHeader from "./WvAppHeader/AppHeader";

import { PrismicProvider } from "@prismicio/react";
import { GoogleReCaptchaProvider } from "@google-recaptcha/react";
import classNames from "classnames";
import dayjs from "dayjs";
import nl from "dayjs/locale/nl.js";
import LocalizedFormat from "dayjs/plugin/localizedFormat.js";
import { DataLayerCtxProvider } from "~/hooks/useDataLayer";
import { useUserEmailCookieSync } from "~/hooks/useUserEmailCookie";
import { useDataLayerPageView } from "~/hooks/useDataLayerPageView";
import { PrismicLabelsCtxProvider } from "~/hooks/usePrismicLabel";
import { PrivateDataContext, useLoadPrivateData } from "~/hooks/usePrivateData";
import { linkResolver } from "~/lib/prismic/utils";
import WvLink from "../atoms/WvLink";
import ExternalScripts from "./ExternalScripts";
import type { loader } from "./Layout.server";
import * as Sentry from "@sentry/remix";
import { getAnonymousVisitorId } from "~/lib/sentryUtils";

import type { CheckoutCustomLineItem } from "@commercekit/api";
import { useCheckout } from "@commercekit/remix";
import {
  CheckoutAbandonmentProvider,
  useCheckoutAbandonment,
  useLayoutCheckoutTracking,
} from "~/hooks/abandonCart";
import { LoqateProvider } from "~/hooks/useLoqate";
import { useReturnFromExternalStrict } from "~/hooks/useReturnFromExternal";
import WvPromoBar from "./WvPromoBar";
import WvStoreSuggestion from "../organisms/WvStoreSuggestion";
import { getTrustedShopsId } from "~/lib/trustedShops";
import type { LayoutDocumentDataSocialMediasItem } from "~/lib/prismic";

// Type for the data passed to child routes via <Outlet context={...} />
export interface OutletContext {
  socialMedias: LayoutDocumentDataSocialMediasItem[];
  languages: { domain: string; country: string; defaultLang: string; locale: string }[];
  locale: string;
  contactText: string;
  logoUrl: string;
  headerRating?: { rating: number; reviewCount: number } | null;
}

dayjs.extend(LocalizedFormat);
dayjs.locale(nl);

const DocumentWithAbandonmentTracking: FC<
  React.PropsWithChildren<{
    Header?: React.ReactNode;
    PromoBar?: React.ReactNode;
    Footer?: React.ReactNode;
    title?: TitleField;
    gtmID?: string;
    cookieConsentText?: RichTextField;
    cookieConsentAllow?: RichTextField;
    cookieConsentDismiss?: RichTextField;
    cookieConsentPrivacyText?: RichTextField;
    cookieConsentPrivacyLink?: RichTextField;
    trengoKey?: string;
    locale: string;
    isKlaviyoEnabled: boolean;
    isYotpoEnabled: boolean;
    isTrustedShopsEnabled: boolean;
    isTaggrsEnabled: boolean;
  }>
> = (props) => {
  useUserEmailCookieSync();
  const { checkout } = useCheckout();
  const { resetAbandonmentTracking, handlePaymentReturnAbandonment } =
    useCheckoutAbandonment();
  const [lastCartSignature, setLastCartSignature] = React.useState<
    string | null
  >(null);

  React.useEffect(() => {
    const currentSignature = JSON.stringify({
      itemCount: checkout?.lineItems?.length,
      items: checkout?.lineItems?.map(
        (item: CheckoutCustomLineItem) => `${item.id}:${item.quantity}`
      ),
    });

    // Only reset if cart content actually changed
    if (currentSignature !== lastCartSignature && lastCartSignature !== null) {
      resetAbandonmentTracking();
    }

    setLastCartSignature(currentSignature);
  }, [checkout?.lineItems]);

  useLayoutCheckoutTracking();

  useReturnFromExternalStrict(() => {
    // Check if we returned from payment and handle abandonment if needed
    handlePaymentReturnAbandonment();
  });

  return <Document {...props} />;
};

export const Document: FC<
  React.PropsWithChildren<{
    Header?: React.ReactNode;
    PromoBar?: React.ReactNode;
    Footer?: React.ReactNode;
    title?: TitleField;
    gtmID?: string;
    cookieConsentText?: RichTextField;
    cookieConsentAllow?: RichTextField;
    cookieConsentDismiss?: RichTextField;
    cookieConsentPrivacyText?: RichTextField;
    cookieConsentPrivacyLink?: RichTextField;
    trengoKey?: string;
    locale: string;
    isKlaviyoEnabled: boolean;
    isYotpoEnabled: boolean;
    isTrustedShopsEnabled: boolean;
    isTaggrsEnabled: boolean;
  }>
> = ({
  children,
  Header,
  PromoBar,
  Footer,
  gtmID,
  cookieConsentText,
  cookieConsentDismiss,
  cookieConsentPrivacyText,
  cookieConsentPrivacyLink,
  trengoKey,
  locale,
  isKlaviyoEnabled,
  isYotpoEnabled,
  isTrustedShopsEnabled,
  isTaggrsEnabled,
}) => {
  const {
    dispatch: { closeMenu },
  } = useGlobalCtx();
  const location = useLocation();
  const { pathname } = location;
  const navigationType = useNavigationType();
  const bodyScrollPositions = React.useRef<Record<string, number>>({});
  const previousPathnameRef = React.useRef(pathname);
  const localePath = useLocalePath();
  const { localeFullName } = useLocale();

  const language: string = localeFullName
    .split("-")
    .map((part: string, index: number) =>
      index === 1 ? part.toUpperCase() : part.toLowerCase()
    )
    .join("-");

  //SHOW HEADER ON CHECKOUT PAGE TOO (till design says otherwise)
  const hideHeader = useMemo(
    () => [localePath(`/checkout`)].includes(pathname) && false,
    [localePath, pathname]
  );

  useDataLayerPageView(pathname);

  // body (not window) is the scroll container in custom.css, so Remix's
  // <ScrollRestoration /> can't reset it on its own.
  
  React.useEffect(() => {
    const key = location.key;
    const savedPosition = bodyScrollPositions.current[key];
    const isSamePathname = previousPathnameRef.current === pathname;
    previousPathnameRef.current = pathname;

    // Restore/Reset scroll position
    if (navigationType === "POP" && savedPosition != null) {
      document.body.scrollTop = savedPosition;
    } else if (!isSamePathname) {
      document.body.scrollTop = 0;
    }

    return () => {
      bodyScrollPositions.current[key] = document.body.scrollTop;
    };
  }, [location.key, navigationType, pathname]);

  //TODO: Temporay cookie consent notice
  const [cookieConsentStatus, setCookieConsentStatus] =
    React.useState<string>("dismiss");
  React.useEffect(() => {
    const pair = document.cookie
      .split("; ")
      .find((x: any) => x.startsWith("cookieconsent_status="));
    if (pair) {
      const consentStatus = pair.split("=")[1];
      setCookieConsentStatus(consentStatus);
    } else {
      setCookieConsentStatus("");
    }
  }, []);

  const onCookieConsentAllowed = (e: any) => {
    e.stopPropagation();
    document.cookie = "cookieconsent_status=dismiss";
    setCookieConsentStatus("dismiss");
  };

  const trustedShopsId = getTrustedShopsId(localeFullName);

  return (
    <html lang={language}>
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <meta name="color-scheme" content="light only"></meta>
        {localeFullName === "nl-nl" && (
          <>
            <script src="//cdn-4.convertexperiments.com/v1/js/1004236-100415395.js?environment=production" />
            <script>{`window._conv_q = window._conv_q || [];`}</script>
          </>
        )}

        <Meta />
        <Links />
      </head>
      <body suppressHydrationWarning onClick={() => closeMenu}>
        <ExternalScripts
          gtmID={gtmID}
          trengoKey={trengoKey}
          allowHotJar={true}
          locale={locale}
          isKlaviyoEnabled={isKlaviyoEnabled}
          isYotpoEnabled={isYotpoEnabled}
          isTaggrsEnabled={isTaggrsEnabled}
        />
        {![
          "pt-pt",
          "es-es",
          "it-it",
          "en-gb",
          "en-ie",
          "da-dk",
          "sv-se",
          "pl-pl",
          "fr-fr",
          "de-de",
          "nl-be",
          "nl-nl",
        ].includes(locale) &&
          cookieConsentStatus != "dismiss" && (
            <div
              role="dialog"
              aria-live="polite"
              aria-label="cookieconsent"
              aria-describedby="cookieconsent:desc"
              className={classNames(
                "cc-window cc-banner cc-type-info cc-theme-block cc-bottom",
                { "cc-invisible": cookieConsentStatus === "dismiss" }
              )}
            >
              {/* <!--googleoff: all--> */}
              <span id="cookieconsent:desc" className="cc-message">
                {asText(cookieConsentText)}
                <a
                  aria-label="learn more about cookies"
                  role="button"
                  tabIndex={0}
                  className="cc-link"
                  href={`/${asText(cookieConsentPrivacyLink)}`}
                  rel="noopener noreferrer nofollow"
                  target="_blank"
                >
                  {asText(cookieConsentPrivacyText)}
                </a>
              </span>
              <div className="cc-compliance">
                <a
                  c-compliance
                  aria-label="dismiss cookie message"
                  role="button"
                  tabIndex={0}
                  className="cc-btn cc-dismiss"
                  onClick={(e: any) => onCookieConsentAllowed(e)}
                >
                  {asText(cookieConsentDismiss)}
                </a>
              </div>
            </div>
          )}

        <ErrorAlertCtxProvider>
          {hideHeader ? null : Header}
          {PromoBar}
          <main>{children}</main>
          {Footer}
          <WvStoreSuggestion locale={locale} />
        </ErrorAlertCtxProvider>
        <ScrollRestoration />
        <Scripts />
        {isTrustedShopsEnabled && trustedShopsId && (
          <script
            async
            data-desktop-y-offset="0"
            data-mobile-y-offset="0"
            data-desktop-disable-reviews="false"
            data-desktop-enable-custom="false"
            data-desktop-position="right"
            data-desktop-custom-width="156"
            data-desktop-enable-fadeout="false"
            data-disable-mobile="false"
            data-disable-trustbadge="true"
            data-mobile-custom-width="156"
            data-mobile-disable-reviews="false"
            data-mobile-enable-custom="false"
            data-mobile-position="left"
            data-mobile-enable-topbar="false"
            data-mobile-enable-fadeout="true"
            data-color-scheme="light"
            charSet="UTF-8"
            src={`//widgets.trustedshops.com/js/${trustedShopsId}.js`}
          ></script>
        )}
      </body>
    </html>
  );
};
const InternalLinkComponent: React.ElementType<LinkProps> = ({
  href,
  children,
  ...props
}) => {
  const localePath = useLocalePath();
  return (
    <WvLink prefetch="intent" to={localePath(href)} {...props}>
      {children}
    </WvLink>
  );
};
export const Root: FC = () => {
  const {
    category,
    header,
    footer,
    checkoutSimple,
    checkout,
    pt,
    customer,
    global,
    origin,
    locale,
    onBehalf,
    languageNames,
    languages,
    gtm: { currencyCode, brand, gtmID },
    cookieConsent: { text, allow, dismiss, privacyText, privacyLink },
    promoBar,
    trengoKey,
    sooqrAccount,
    isKlaviyoEnabled,
    isYotpoEnabled,
    isTrustedShopsEnabled,
    isTaggrsEnabled,
    captchaSiteKey,
    etrustedData,
    reviews,
    customerReviews,
  } = useLoaderData<typeof loader>();
  const privateData = useLoadPrivateData();
  const currentLocation = useLocation();

  const removeDecimal = (e: any) => {
    const value = e.target.value;
    // If the value includes a decimal point, remove it
    if (value.includes(".")) {
      e.target.value = value.split(".")[0];
    }
  };

  React.useEffect(() => {
    if (customer) {
      Sentry.setUser({
        id: String(customer.id),
        email: String(customer.email),
        username:
          [customer.firstName, customer.middleName, customer.lastName]
            .filter(Boolean)
            .map(String)
            .join(" ")
            .trim() || undefined,
        type: String(customer.type),
      });
    } else {
      Sentry.setUser({ id: getAnonymousVisitorId() });
    }
  }, [
    customer?.id,
    customer?.email,
    customer?.firstName,
    customer?.middleName,
    customer?.lastName,
    customer?.type,
  ]);

  React.useEffect(() => {
    Sentry.setTag("locale", locale);
  }, [locale]);

  React.useEffect(() => {
    document?.querySelectorAll("input[type=number]")?.forEach((input) => {
      input.addEventListener("input", removeDecimal);
    });
    return () => {
      document?.querySelectorAll("input[type=number]")?.forEach((input) => {
        input.removeEventListener("input", removeDecimal);
      });
    };
  }, [currentLocation]);
  return (
    <PrivateDataContext.Provider value={privateData}>
      <DataLayerCtxProvider brand={brand} currencyCode={currencyCode}>
        <RouteCtxProvider locale={locale}>
          <GlobalCtxProvider global={global}>
            <CheckoutAbandonmentProvider>
              <LoqateProvider apiKeys={global.loqateApiKeys}>
                <PrismicLabelsCtxProvider pt={pt}>
                  <GoogleReCaptchaProvider
                    type="v2-checkbox"
                    siteKey={captchaSiteKey}
                    language={locale}
                  >
                    <PrismicProvider
                      linkResolver={linkResolver}
                      internalLinkComponent={InternalLinkComponent}
                      externalLinkComponent={({ href, children, ...props }) => {
                        const url = new URL(href);
                        return origin === url.origin ? (
                          <WvLink
                            prefetch="intent"
                            to={`${url.pathname}${url.search}${url.hash}`}
                            {...props}
                          >
                            {children}
                          </WvLink>
                        ) : (
                          <a
                            href={href}
                            target="_blank"
                            rel="noreferrer"
                            {...props}
                          >
                            {children}
                          </a>
                        );
                      }}
                    >
                      <CommercekitCtx
                        checkoutSimple={checkoutSimple}
                        checkout={checkout}
                        customer={customer}
                      >
                        <DocumentWithAbandonmentTracking
                          Header={
                            <AppHeader
                              {...header}
                              category={category}
                              onBehalf={onBehalf}
                              languages={languages}
                              languageNames={languageNames}
                              locale={locale}
                              headerUspBar={header.navLinks}
                              sooqrAccount={sooqrAccount}
                              headerRating={etrustedData?.headerAllTimeRating}
                            />
                          }
                          PromoBar={<WvPromoBar {...promoBar} />}
                          Footer={<AppFooter {...footer} headerRating={etrustedData?.headerRating} brandImage={reviews?.brandImage} customerReviews={customerReviews} />}
                          gtmID={gtmID}
                          cookieConsentText={text}
                          cookieConsentAllow={allow}
                          cookieConsentDismiss={dismiss}
                          cookieConsentPrivacyText={privacyText}
                          cookieConsentPrivacyLink={privacyLink}
                          trengoKey={trengoKey}
                          locale={locale}
                          isKlaviyoEnabled={isKlaviyoEnabled}
                          isYotpoEnabled={isYotpoEnabled}
                          isTrustedShopsEnabled={isTrustedShopsEnabled}
                          isTaggrsEnabled={isTaggrsEnabled}
                        >
                          <Outlet
                            context={{
                              socialMedias: footer.socialMedias,
                              languages,
                              locale,
                              contactText: asText(footer.contact) || "",
                              logoUrl: header.logo?.url || "",
                              headerRating: etrustedData?.headerRating,
                            }}
                          />
                        </DocumentWithAbandonmentTracking>
                      </CommercekitCtx>
                    </PrismicProvider>
                  </GoogleReCaptchaProvider>
                </PrismicLabelsCtxProvider>
              </LoqateProvider>
            </CheckoutAbandonmentProvider>
          </GlobalCtxProvider>
        </RouteCtxProvider>
      </DataLayerCtxProvider>
    </PrivateDataContext.Provider>
  );
};
